import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  ContentRecommendation,
  RecommendationFeedback,
  ContextualFactor,
  BanditsState,
  AIError,
  ContentType,
} from '@coffeeplexity/shared';
import type { RootState } from '../index';

interface RecommendationEngineSliceState {
  activeRecommendations: ContentRecommendation[];
  dismissedRecommendations: string[];
  feedbackHistory: RecommendationFeedback[];
  contextualFactors: ContextualFactor[];
  banditsState: BanditsState | null;
  loading: {
    recommendations: boolean;
    feedback: boolean;
    context: boolean;
    bandits: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  cacheExpiry: string | null;
  explorationRate: number;
  personalizedFilters: Record<string, any>;
}

const initialState: RecommendationEngineSliceState = {
  activeRecommendations: [],
  dismissedRecommendations: [],
  feedbackHistory: [],
  contextualFactors: [],
  banditsState: null,
  loading: {
    recommendations: false,
    feedback: false,
    context: false,
    bandits: false,
  },
  error: null,
  lastUpdated: null,
  cacheExpiry: null,
  explorationRate: 0.1,
  personalizedFilters: {},
};

// Async thunks
export const fetchRecommendations = createAsyncThunk(
  'recommendationEngine/fetchRecommendations',
  async (
    {
      userId,
      context,
      limit = 10,
      contentTypes,
      excludeIds,
      skillCategories,
      difficultyRange,
      timeRange,
      confidenceThreshold,
    }: {
      userId: string;
      context?: Record<string, any>;
      limit?: number;
      contentTypes?: ContentType[];
      excludeIds?: string[];
      skillCategories?: string[];
      difficultyRange?: [number, number];
      timeRange?: [number, number];
      confidenceThreshold?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );

      const response = await recommendationApi.fetchRecommendations({
        userId,
        context,
        limit,
        contentTypes,
        excludeIds,
        skillCategories,
        difficultyRange,
        timeRange,
        confidenceThreshold,
      });

      return response.recommendations;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'RECOMMENDATIONS_FETCH_ERROR',
        message: error.message || 'Failed to fetch recommendations',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const submitRecommendationFeedback = createAsyncThunk(
  'recommendationEngine/submitFeedback',
  async (
    feedback: Omit<RecommendationFeedback, 'id' | 'timestamp'>,
    { rejectWithValue }
  ) => {
    try {
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );

      const response = await recommendationApi.submitFeedback(feedback);
      return response.feedback;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'FEEDBACK_SUBMIT_ERROR',
        message: error.message || 'Failed to submit recommendation feedback',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateContextualFactors = createAsyncThunk(
  'recommendationEngine/updateContext',
  async (
    {
      userId,
      factors,
    }: {
      userId: string;
      factors: Partial<ContextualFactor>[];
    },
    { rejectWithValue }
  ) => {
    try {
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );

      const response = await recommendationApi.updateContextualFactors(
        userId,
        factors
      );
      return response.factors;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CONTEXT_UPDATE_ERROR',
        message: error.message || 'Failed to update contextual factors',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchBanditsState = createAsyncThunk(
  'recommendationEngine/fetchBanditsState',
  async (userId: string, { rejectWithValue }) => {
    try {
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );

      const banditsState = await recommendationApi.fetchBanditsState(userId);
      return banditsState;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'BANDITS_FETCH_ERROR',
        message: error.message || 'Failed to fetch bandits state',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const exploreRecommendation = createAsyncThunk(
  'recommendationEngine/explore',
  async (
    {
      userId,
      armId,
      context,
    }: {
      userId: string;
      armId: string;
      context?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );

      const response = await recommendationApi.exploreRecommendation(
        userId,
        armId,
        context
      );
      return response.recommendation;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'EXPLORATION_ERROR',
        message: error.message || 'Failed to explore recommendation',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const refreshRecommendations = createAsyncThunk(
  'recommendationEngine/refresh',
  async (
    {
      userId,
      forceRefresh = false,
    }: {
      userId: string;
      forceRefresh?: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const currentRecommendations =
        state.recommendationEngine.activeRecommendations;

      // If not forcing refresh and we have recent recommendations, return them
      if (!forceRefresh && currentRecommendations.length > 0) {
        const cacheExpiry = state.recommendationEngine.cacheExpiry;
        if (cacheExpiry && new Date() < new Date(cacheExpiry)) {
          return currentRecommendations;
        }
      }

      // Use the API service for refresh
      const { recommendationApi } = await import(
        '../../services/api/recommendationApi'
      );
      const response = await recommendationApi.refreshRecommendations(
        userId,
        forceRefresh
      );
      return response.recommendations;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'RECOMMENDATIONS_REFRESH_ERROR',
        message: error.message || 'Failed to refresh recommendations',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

const recommendationEngineSlice = createSlice({
  name: 'recommendationEngine',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearRecommendations: state => {
      state.activeRecommendations = [];
      state.dismissedRecommendations = [];
      state.feedbackHistory = [];
      state.lastUpdated = null;
      state.cacheExpiry = null;
    },
    dismissRecommendation: (
      state,
      action: PayloadAction<{ recommendationId: string; reason?: string }>
    ) => {
      const { recommendationId, reason } = action.payload;
      state.dismissedRecommendations.push(recommendationId);
      state.activeRecommendations = state.activeRecommendations.filter(
        rec => rec.id !== recommendationId
      );

      // Add implicit feedback
      const feedback: RecommendationFeedback = {
        id: `feedback_${Date.now()}`,
        recommendationId,
        userId: '', // Will be set by the component
        feedbackType: 'dismissed',
        reason: reason || '',
        timestamp: new Date().toISOString(),
        context: { dismissedAt: new Date().toISOString() },
      };
      state.feedbackHistory.unshift(feedback);
    },
    acceptRecommendation: (
      state,
      action: PayloadAction<{ recommendationId: string; rating?: number }>
    ) => {
      const { recommendationId, rating } = action.payload;
      const recommendation = state.activeRecommendations.find(
        rec => rec.id === recommendationId
      );

      if (recommendation) {
        recommendation.metadata = {
          ...recommendation.metadata,
          accepted: true,
          acceptedAt: new Date().toISOString(),
          rating,
        };

        // Add implicit feedback
        const feedback: RecommendationFeedback = {
          id: `feedback_${Date.now()}`,
          recommendationId,
          userId: '', // Will be set by the component
          feedbackType: 'accepted',
          rating: rating || 0,
          timestamp: new Date().toISOString(),
          context: { acceptedAt: new Date().toISOString() },
        };
        state.feedbackHistory.unshift(feedback);
      }
    },
    updatePersonalizedFilters: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.personalizedFilters = {
        ...state.personalizedFilters,
        ...action.payload,
      };
    },
    setExplorationRate: (state, action: PayloadAction<number>) => {
      state.explorationRate = Math.max(0, Math.min(1, action.payload));
    },
    markRecommendationViewed: (state, action: PayloadAction<string>) => {
      const recommendation = state.activeRecommendations.find(
        rec => rec.id === action.payload
      );
      if (recommendation) {
        recommendation.metadata = {
          ...recommendation.metadata,
          viewed: true,
          viewedAt: new Date().toISOString(),
        };
      }
    },
    updateContextualFactor: (
      state,
      action: PayloadAction<ContextualFactor>
    ) => {
      const existingIndex = state.contextualFactors.findIndex(
        factor => factor.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.contextualFactors[existingIndex] = action.payload;
      } else {
        state.contextualFactors.push(action.payload);
      }
    },
    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },
  },
  extraReducers: builder => {
    // Fetch Recommendations
    builder
      .addCase(fetchRecommendations.pending, state => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.activeRecommendations = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 20 * 60 * 1000).toISOString(); // 20 minutes
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as AIError;
      });

    // Submit Recommendation Feedback
    builder
      .addCase(submitRecommendationFeedback.pending, state => {
        state.loading.feedback = true;
        state.error = null;
      })
      .addCase(submitRecommendationFeedback.fulfilled, (state, action) => {
        state.loading.feedback = false;
        state.feedbackHistory.unshift(action.payload);
        // Keep only the most recent 100 feedback items
        if (state.feedbackHistory.length > 100) {
          state.feedbackHistory = state.feedbackHistory.slice(0, 100);
        }
      })
      .addCase(submitRecommendationFeedback.rejected, (state, action) => {
        state.loading.feedback = false;
        state.error = action.payload as AIError;
      });

    // Update Contextual Factors
    builder
      .addCase(updateContextualFactors.pending, state => {
        state.loading.context = true;
        state.error = null;
      })
      .addCase(updateContextualFactors.fulfilled, (state, action) => {
        state.loading.context = false;
        state.contextualFactors = action.payload;
      })
      .addCase(updateContextualFactors.rejected, (state, action) => {
        state.loading.context = false;
        state.error = action.payload as AIError;
      });

    // Fetch Bandits State
    builder
      .addCase(fetchBanditsState.pending, state => {
        state.loading.bandits = true;
        state.error = null;
      })
      .addCase(fetchBanditsState.fulfilled, (state, action) => {
        state.loading.bandits = false;
        state.banditsState = action.payload;
        state.explorationRate = action.payload.explorationRate;
      })
      .addCase(fetchBanditsState.rejected, (state, action) => {
        state.loading.bandits = false;
        state.error = action.payload as AIError;
      });

    // Explore Recommendation
    builder
      .addCase(exploreRecommendation.pending, state => {
        state.loading.recommendations = true;
      })
      .addCase(exploreRecommendation.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        // Add the explored recommendation to the active list
        state.activeRecommendations.unshift(action.payload);
        // Mark it as explored
        action.payload.metadata = {
          ...action.payload.metadata,
          explored: true,
          exploredAt: new Date().toISOString(),
        };
      })
      .addCase(exploreRecommendation.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as AIError;
      });

    // Refresh Recommendations
    builder
      .addCase(refreshRecommendations.pending, state => {
        state.loading.recommendations = true;
      })
      .addCase(refreshRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.activeRecommendations = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 20 * 60 * 1000).toISOString();
      })
      .addCase(refreshRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as AIError;
      });
  },
});

export const {
  clearError,
  clearRecommendations,
  dismissRecommendation,
  acceptRecommendation,
  updatePersonalizedFilters,
  setExplorationRate,
  markRecommendationViewed,
  updateContextualFactor,
  setCacheExpiry,
} = recommendationEngineSlice.actions;

// Basic selectors
export const selectActiveRecommendations = (state: RootState) =>
  state.recommendationEngine.activeRecommendations;
export const selectDismissedRecommendations = (state: RootState) =>
  state.recommendationEngine.dismissedRecommendations;
export const selectFeedbackHistory = (state: RootState) =>
  state.recommendationEngine.feedbackHistory;
export const selectContextualFactors = (state: RootState) =>
  state.recommendationEngine.contextualFactors;
export const selectBanditsState = (state: RootState) =>
  state.recommendationEngine.banditsState;
export const selectRecommendationLoading = (state: RootState) =>
  state.recommendationEngine.loading;
export const selectRecommendationError = (state: RootState) =>
  state.recommendationEngine.error;
export const selectExplorationRate = (state: RootState) =>
  state.recommendationEngine.explorationRate;
export const selectPersonalizedFilters = (state: RootState) =>
  state.recommendationEngine.personalizedFilters;

// Memoized selectors
export const selectRecommendationsByType = createSelector(
  [selectActiveRecommendations],
  recommendations => {
    return recommendations.reduce(
      (acc, rec) => {
        if (!acc[rec.contentType]) {
          acc[rec.contentType] = [];
        }
        if (!acc[rec.contentType]) {
          acc[rec.contentType] = [];
        }
        acc[rec.contentType].push(rec);
        return acc;
      },
      {} as Record<string, ContentRecommendation[]>
    );
  }
);

export const selectHighConfidenceRecommendations = createSelector(
  [selectActiveRecommendations],
  recommendations => {
    return recommendations.filter(rec => rec.confidence >= 0.8);
  }
);

export const selectUnviewedRecommendations = createSelector(
  [selectActiveRecommendations],
  recommendations => {
    return recommendations.filter(rec => !rec.metadata?.['viewed']);
  }
);

export const selectRecommendationsBySkillAlignment = createSelector(
  [selectActiveRecommendations],
  recommendations => {
    return recommendations.reduce(
      (acc, rec) => {
        rec.skillAlignment.forEach(skill => {
          if (!acc[skill.skillName]) {
            acc[skill.skillName] = [];
          }
          if (!acc[skill.skillName]) {
            acc[skill.skillName] = [];
          }
          acc[skill.skillName].push(rec);
        });
        return acc;
      },
      {} as Record<string, ContentRecommendation[]>
    );
  }
);

export const selectRecentFeedback = createSelector(
  [selectFeedbackHistory],
  feedbackHistory => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return feedbackHistory.filter(
      feedback => new Date(feedback.timestamp) > oneWeekAgo
    );
  }
);

export const selectFeedbackByType = createSelector(
  [selectFeedbackHistory],
  feedbackHistory => {
    return feedbackHistory.reduce(
      (acc, feedback) => {
        if (!acc[feedback.feedbackType]) {
          acc[feedback.feedbackType] = [];
        }
        if (!acc[feedback.feedbackType]) {
          acc[feedback.feedbackType] = [];
        }
        acc[feedback.feedbackType].push(feedback);
        return acc;
      },
      {} as Record<string, RecommendationFeedback[]>
    );
  }
);

export const selectContextByType = createSelector(
  [selectContextualFactors],
  contextualFactors => {
    return contextualFactors.reduce(
      (acc, factor) => {
        if (!acc[factor.type]) {
          acc[factor.type] = [];
        }
        if (!acc[factor.type]) {
          acc[factor.type] = [];
        }
        acc[factor.type].push(factor);
        return acc;
      },
      {} as Record<string, ContextualFactor[]>
    );
  }
);

export const selectBestPerformingArms = createSelector(
  [selectBanditsState],
  banditsState => {
    if (!banditsState) return [];
    return banditsState.arms
      .filter(arm => arm.pulls > 0)
      .sort((a, b) => b.averageReward - a.averageReward)
      .slice(0, 5);
  }
);

export const selectIsRecommendationsCacheStale = createSelector(
  [
    selectActiveRecommendations,
    (state: RootState) => state.recommendationEngine.cacheExpiry,
  ],
  (recommendations, cacheExpiry) => {
    if (recommendations.length === 0 || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export const selectRecommendationsNeedingFeedback = createSelector(
  [selectActiveRecommendations, selectFeedbackHistory],
  (recommendations, feedbackHistory) => {
    const feedbackRecommendationIds = new Set(
      feedbackHistory.map(feedback => feedback.recommendationId)
    );
    return recommendations.filter(
      rec => rec.metadata?.['viewed'] && !feedbackRecommendationIds.has(rec.id)
    );
  }
);

export default recommendationEngineSlice.reducer;
